import { generateClient } from 'aws-amplify/api';
import {
  listGalleries,
  getGallery,
  listGalleryImages
} from '../graphql/queries';
import {
  createGallery,
  updateGallery,
  deleteGallery,
  createGalleryImage,
  updateGalleryImage,
  deleteGalleryImage
} from '../graphql/mutations';

// Lazy client generation to ensure Amplify is configured first
const getClient = () => {
  try {
    return generateClient();
  } catch (error) {
    console.error('GraphQL client not available:', error.message);
    throw error;
  }
};

export // Staff-only mutations below state authMode: 'userPool' rather than
// inheriting whatever the default is at call time. Reads here are public, so
// the default suits them; only the writes need the signed-in token.
class GalleryService {
  /**
   * Fetch all galleries for public display
   */
  static async fetchPublicGalleries(limit = 20) {
    try {
      const client = getClient();
      const response = await client.graphql({
        query: listGalleries,
        variables: {
          filter: { isActive: { eq: true } },
          limit,
          // Sort by displayOrder first, then by date (newest first)
        }
      });

      // Sort galleries by displayOrder and date
      const galleries = response.data.listGalleries.items.sort((a, b) => {
        if (a.displayOrder !== b.displayOrder) {
          return (a.displayOrder || 999) - (b.displayOrder || 999);
        }
        return new Date(b.date || b.createdAt) - new Date(a.date || a.createdAt);
      });

      return {
        ...response.data.listGalleries,
        items: galleries
      };
    } catch (error) {
      console.error('Error fetching public galleries:', error);
      return { items: [], nextToken: null };
    }
  }

  /**
   * Fetch all galleries for admin management
   */
  static async fetchAllGalleries(limit = 50) {
    try {
      const client = getClient();
      const response = await client.graphql({
        query: listGalleries,
        variables: { limit }
      });

      // Sort galleries by displayOrder and date
      const galleries = response.data.listGalleries.items.sort((a, b) => {
        if (a.displayOrder !== b.displayOrder) {
          return (a.displayOrder || 999) - (b.displayOrder || 999);
        }
        return new Date(b.date || b.createdAt) - new Date(a.date || a.createdAt);
      });

      return {
        ...response.data.listGalleries,
        items: galleries
      };
    } catch (error) {
      console.error('Error fetching galleries:', error);
      return { items: [], nextToken: null };
    }
  }

  /**
   * Fetch a single gallery with all images
   */
  static async fetchGallery(galleryId) {
    try {
      const client = getClient();
      
      // Fetch gallery details
      const galleryResponse = await client.graphql({
        query: getGallery,
        variables: { id: galleryId }
      });

      if (!galleryResponse.data.getGallery) {
        throw new Error('Gallery not found');
      }

      // Fetch all images for this gallery
      const imagesResponse = await client.graphql({
        query: listGalleryImages,
        variables: {
          filter: { galleryId: { eq: galleryId } },
          limit: 100 // Should be enough for most galleries
        }
      });

      // Sort images by order
      const images = imagesResponse.data.listGalleryImages.items.sort((a, b) => 
        (a.order || 0) - (b.order || 0)
      );

      return {
        ...galleryResponse.data.getGallery,
        images: { items: images }
      };
    } catch (error) {
      console.error('Error fetching gallery:', error);
      throw error;
    }
  }

  /**
   * Create a new gallery
   */
  static async createGallery(galleryData) {
    try {
      const client = getClient();
      const response = await client.graphql({
        query: createGallery,
        authMode: 'userPool',
        variables: { input: galleryData }
      });

      return response.data.createGallery;
    } catch (error) {
      console.error('Error creating gallery:', error);
      throw error;
    }
  }

  /**
   * Update an existing gallery
   */
  static async updateGallery(galleryId, updateData) {
    try {
      const client = getClient();
      const response = await client.graphql({
        query: updateGallery,
        authMode: 'userPool',
        variables: {
          input: {
            id: galleryId,
            ...updateData
          }
        }
      });

      return response.data.updateGallery;
    } catch (error) {
      console.error('Error updating gallery:', error);
      throw error;
    }
  }

  /**
   * Delete a gallery and all its images
   */
  static async deleteGallery(galleryId) {
    try {
      const client = getClient();
      
      // First, delete all images in the gallery
      const imagesResponse = await client.graphql({
        query: listGalleryImages,
        variables: {
          filter: { galleryId: { eq: galleryId } }
        }
      });

      // Delete all images
      for (const image of imagesResponse.data.listGalleryImages.items) {
        await this.deleteGalleryImage(image.id);
      }

      // Then delete the gallery
      const response = await client.graphql({
        query: deleteGallery,
        authMode: 'userPool',
        variables: { input: { id: galleryId } }
      });

      return response.data.deleteGallery;
    } catch (error) {
      console.error('Error deleting gallery:', error);
      throw error;
    }
  }

  /**
   * Add image to gallery
   */
  static async addImageToGallery(galleryId, imageData) {
    try {
      const client = getClient();
      const response = await client.graphql({
        query: createGalleryImage,
        authMode: 'userPool',
        variables: {
          input: {
            galleryId,
            ...imageData
          }
        }
      });

      return response.data.createGalleryImage;
    } catch (error) {
      console.error('Error adding image to gallery:', error);
      throw error;
    }
  }

  /**
   * Update gallery image
   */
  static async updateGalleryImage(imageId, updateData) {
    try {
      const client = getClient();
      const response = await client.graphql({
        query: updateGalleryImage,
        authMode: 'userPool',
        variables: {
          input: {
            id: imageId,
            ...updateData
          }
        }
      });

      return response.data.updateGalleryImage;
    } catch (error) {
      console.error('Error updating gallery image:', error);
      throw error;
    }
  }

  /**
   * Delete gallery image
   */
  static async deleteGalleryImage(imageId) {
    try {
      const client = getClient();
      const response = await client.graphql({
        query: deleteGalleryImage,
        authMode: 'userPool',
        variables: { input: { id: imageId } }
      });

      return response.data.deleteGalleryImage;
    } catch (error) {
      console.error('Error deleting gallery image:', error);
      throw error;
    }
  }

  /**
   * Get gallery categories for form dropdowns
   */
  static getGalleryCategories() {
    return [
      { value: 'FARM_LIFE', label: 'Farm Life' },
      { value: 'CROPS', label: 'Crops' },
      { value: 'HARVEST', label: 'Harvest' },
      { value: 'ACTIVITIES', label: 'Activities' },
      { value: 'EVENTS', label: 'Events' },
      { value: 'NATURE', label: 'Nature' },
      { value: 'INFRASTRUCTURE', label: 'Infrastructure' },
      { value: 'PEOPLE', label: 'People' },
      { value: 'SEASONS', label: 'Seasons' },
      { value: 'ORGANIC_PROCESS', label: 'Organic Process' }
    ];
  }
}